import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Card, SkeletonLoader } from '@insight-health-ai/roseate/components';
import { Lightbulb02, Copy02, Printer } from '@insight-health-ai/roseate/icons';
import { Empty, Segmented } from 'antd';
import moment from 'moment';
import {
  parseSummary,
  sanitiseSummaryAsText,
  copyToClipboard,
  sanitiseSummaryAsHtml,
  formatStaticDate,
} from 'src/utils';
import { useReactToPrint } from 'react-to-print';
import {
  useGetSummaryList,
  UseGetSummaryListParams,
  useRateSummary,
  useUpdateSummary,
} from 'src/hooks/summary';
import { showToastSuccess } from 'src/utilities/toast';
import { Encounter } from 'src/models';
import { Rating } from './rating';
import Editor from './editor';
import { debounce } from 'lodash';

type SummaryCardProps = {
  filters: Pick<
    UseGetSummaryListParams,
    'encounterId' | 'encounterPhase' | 'type' | 'serviceRequestId'
  >;
  encounter?: Partial<Pick<Encounter, 'description' | 'created_at'>>;
  title?: string;
  emptyPlaceholder?: string;
  editable?: boolean;
};

export const SummaryCard: React.FC<SummaryCardProps> = ({
  filters,
  title,
  encounter,
  emptyPlaceholder,
  editable = false,
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [activeTab, setActiveTab] = useState<'main' | 'form'>('main');
  const changeDetectedRef = useRef<number | null>(null);

  const { data: { summaries = [] } = {}, isLoading } = useGetSummaryList({
    ...filters,
  });

  const { data: { summaries: formSummaries = [] } = {} } = useGetSummaryList(
    {
      ...filters,
      encounterPhase:
        `${filters.encounterPhase}-forms` as typeof filters.encounterPhase,
    },
    {
      // Only fetch form summaries if the encounter phase exists and is not already a form
      enabled:
        !!filters.encounterPhase && !filters.encounterPhase.endsWith('-forms'),
    }
  );

  const { mutateAsync: updateSummaryMutation, isPending: isUpdatingSummary } =
    useUpdateSummary();

  const summary = summaries?.[0];
  const formSummary = formSummaries?.[0];

  const summaryString = useMemo(() => {
    if (!summary?.summary_json) {
      return '';
    }

    return summary.summary_json;
  }, [summary]);

  const formSummaryString = useMemo(() => {
    if (!formSummaries?.[0]?.summary_json) {
      return '';
    }
    return formSummary.summary_json;
  }, [formSummary]);

  const { mutateAsync: rateSummary } = useRateSummary();

  const handlePrint = useReactToPrint({
    content: () => {
      const content = document.createElement('div');
      content.innerHTML = sanitiseSummaryAsHtml(
        [summaryString, formSummaryString].filter(Boolean).join('<br><br>')
      );
      return content;
    },
    documentTitle: encounter?.description || 'Summary',
    onBeforePrint: () => {
      setIsPrinting(true);
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const updateSummary = useCallback(
    async (value: string) => {
      if (!changeDetectedRef.current) {
        return;
      }
      const currentTime = changeDetectedRef.current;
      await updateSummaryMutation({
        summaryId: summary.summary_id,
        payload: { summary_json: value },
      });
      if (changeDetectedRef.current === currentTime) {
        changeDetectedRef.current = null;
      }
    },
    [summary, updateSummaryMutation]
  );

  const updateSummaryDebounced = useMemo(() => {
    return debounce(updateSummary, 5000);
  }, [updateSummary]);

  const renderSummary = () => {
    let mainSummaryJsx = null;

    if (!summaryString) {
      mainSummaryJsx = (
        <div className="flex justify-center p-4">
          <Empty
            className="max-w-80"
            description={
              emptyPlaceholder ||
              'Summary will be available when patient completes their conversation with lumi'
            }
          />
        </div>
      );
    } else {
      const summaryHtml = parseSummary(summaryString);

      mainSummaryJsx = (
        <>
          {editable ? (
            <Editor
              defaultValue={summaryHtml}
              showToolbar
              onChange={(value) => {
                changeDetectedRef.current = Date.now();
                updateSummaryDebounced(value);
              }}
              onBlur={(_, __, editor) => {
                updateSummary(editor.getHTML());
              }}
              className="h-full w-full"
            />
          ) : (
            <div
              className="pb-10 p-4"
              dangerouslySetInnerHTML={{ __html: summaryHtml }}
            />
          )}
          <div className="absolute bottom-0 right-2">
            <Rating
              label="Rate this note"
              defaultValue={summary?.summary_ratings || 0}
              onCompletion={async ({ value, comment }) => {
                await rateSummary({
                  summary_id: summary.summary_id,
                  score: value,
                  comment,
                });
              }}
            />
          </div>
        </>
      );
    }

    if (!formSummaryString) {
      return mainSummaryJsx;
    }

    const formSummaryHtml = parseSummary(formSummaryString);

    const formSummaryJsx = (
      <div
        className="pb-10 p-4"
        dangerouslySetInnerHTML={{
          __html: formSummaryHtml,
        }}
      />
    );

    return (
      <div className="flex flex-col">
        <div className="sticky top-0 bg-white z-10 px-4 py-2">
          <Segmented
            options={[
              {
                label: 'Summary',
                value: 'main',
              },
              {
                label: 'Forms Summary',
                value: 'form',
              },
            ]}
            value={activeTab}
            onChange={(value) => {
              setActiveTab(value as 'main' | 'form');
            }}
          />
        </div>
        {activeTab === 'main' ? mainSummaryJsx : formSummaryJsx}
      </div>
    );
  };

  return (
    <Card
      outlined={false}
      padding={{
        header: 'sm',
        body: 'none',
      }}
      className="flex-1 min-w-[320px] h-full overflow-hidden"
      title={
        <div className="flex items-center gap-2">
          <Lightbulb02 className="text-primary" />
          {title || 'AI-Generated Summary'}
          {editable && (
            <div className="flex flex-col text-xs text-gray-500 right-0 ml-auto text-right font-normal pr-2">
              <span className="flex flex-col items-end">
                <span className="flex items-center gap-1">
                  {isUpdatingSummary ? (
                    <>
                      <LoadingOutlined />
                      <span>Saving</span>
                    </>
                  ) : (
                    <>
                      <CheckCircleOutlined />
                      <span>Saved</span>
                    </>
                  )}
                </span>
                {summary?.updated_at && (
                  <span>
                    {moment(summary.updated_at).format('MMM DD, YYYY h:mm A')}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      }
      actions={{
        overflowMenu: {
          after: 1,
        },
        list:
          summaryString || formSummaryString
            ? [
                {
                  label: 'Copy',
                  icon: <Copy02 />,
                  onClick: async () => {
                    await copyToClipboard(
                      sanitiseSummaryAsText(
                        [summaryString, formSummaryString]
                          .filter(Boolean)
                          .join('<br><br>')
                      )
                    );
                    showToastSuccess('Summary copied to clipboard');
                  },
                  variant: 'outlined',
                },
                {
                  label: 'Copy as rich text',
                  icon: <Copy02 />,
                  onClick: async () => {
                    await copyToClipboard(
                      sanitiseSummaryAsHtml(
                        [summaryString, formSummaryString]
                          .filter(Boolean)
                          .join('<br><br>'),
                        {
                          prefix: [
                            encounter?.description &&
                              `<strong>Encounter Name:</strong> ${encounter.description}<br>`,
                            encounter?.created_at &&
                              `<strong>Date:</strong> ${formatStaticDate(
                                encounter.created_at,
                                'MMM DD, YYYY'
                              )}`,
                          ]
                            .filter(Boolean)
                            .join('<br />'),
                        }
                      )
                    );
                    showToastSuccess('Summary copied to clipboard');
                  },
                },
                // {
                //   label: 'Regenerate',
                //   icon: <RefreshCw05 />,
                //   onClick: async () => {},
                // },
                {
                  label: 'Print',
                  disabled: isPrinting,
                  icon: isPrinting ? <LoadingOutlined /> : <Printer />,
                  onClick: async () => {
                    handlePrint();
                  },
                },
              ]
            : [],
      }}
    >
      {isLoading ? (
        <div className="p-4">
          <SkeletonLoader active />
        </div>
      ) : (
        renderSummary()
      )}
    </Card>
  );
};
